import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { selectStyles } from "../../types/SelectStyles";
import {
  IOption,
  ITeam,
  IUserData,
} from "../../../../interfaces/team.interface";
import CreatableSelect from "react-select/creatable";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchGroupEmailList } from "./services/fetchGroupEmailList.service";
import { fetchUserGroupEmailList } from "./services/fetchUserGroupEmailList.service";
import { INewRole } from "./interfaces/role.interface";
import { FaTimes } from "react-icons/fa";
import { IUser } from "../../../../../../context/interfaces/User";
import { DeleteButton } from "../../../../../../components/Admin";
import { fetchTeamData } from "../../services/fetchTeamData.service";

interface IAddUserModalProps {
  setShowAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddUser: (newUsers: INewRole[], isMulti: boolean) => void;
  roleOptions: { value: string; label: string }[];
  darkMode: boolean;
  selectedTeam: ITeam | null;
  teamListOptions: IOption[];
  userList: { value: string; label: string }[];
  user: IUser;
}

const AddUserModal: React.FC<IAddUserModalProps> = ({
  setShowAddUserModal,
  handleAddUser,
  roleOptions,
  darkMode,
  selectedTeam,
  teamListOptions,
  userList,
  user,
}) => {
  const { t } = useTranslation();

  const viewerRole = useMemo(
    () =>
      roleOptions.find(
        (opt) =>
          String(opt.value) === "3" || opt.label.toLowerCase() === "viewer",
      ) ?? roleOptions[roleOptions.length - 1],
    [roleOptions],
  );

  const initialTeamValue = selectedTeam ? String(selectedTeam.id) : "";

  const [isSingleUserMode, setIsSingleUserMode] = useState(true);
  const [email, setEmail] = useState("");
  const [distributionEmail, setDistributionEmail] = useState<IOption | null>(
    null,
  );
  const [selectedRole, setSelectedRole] = useState(viewerRole);
  const [team, setTeam] = useState<string>(initialTeamValue);
  const [extractedUsers, setExtractedUsers] = useState<INewRole[]>([]);

  const teamOptions = useMemo(() => {
    const hasEmptyOption = teamListOptions.some((o) => o.value === "");
    const base =
      user.is_superuser && !selectedTeam && !hasEmptyOption
        ? [{ label: "-", value: "" }, ...teamListOptions]
        : teamListOptions;

    if (
      selectedTeam &&
      !base.some((o) => String(o.value) === String(selectedTeam.id))
    ) {
      return [
        ...base,
        { label: selectedTeam.name, value: String(selectedTeam.id) },
      ];
    }

    return base;
  }, [user.is_superuser, selectedTeam, teamListOptions]);

  const selectedTeamOption = useMemo(() => {
    if (!team) {
      return teamOptions.find((o) => o.value === "") ?? null;
    }

    return (
      teamOptions.find((o) => String(o.value) === String(team)) ??
      (selectedTeam && String(selectedTeam.id) === String(team)
        ? { label: selectedTeam.name, value: String(selectedTeam.id) }
        : null)
    );
  }, [teamOptions, team, selectedTeam]);

  const isTeamSelected = !!team;

  const commonStyles = useMemo(
    () => ({
      ...selectStyles(darkMode),
      option: (base: any, { isFocused }: any) => ({
        ...base,
        backgroundColor: isFocused
          ? "#86BC25"
          : darkMode
            ? "#374151"
            : "#FFFFFF",
        color: darkMode ? "#FFFFFF" : "#000000",
      }),
    }),
    [darkMode],
  );

  const { data: teamData } = useQuery({
    queryKey: ["teamData", team],
    queryFn: () => fetchTeamData(Number(team)),
    enabled: !!team,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const existingTeamEmails = useMemo(
    () =>
      teamData?.roles?.map((r: any) => r.user?.email?.toLowerCase()).filter(Boolean) ?? [],
    [teamData],
  );

  useEffect(() => {
    const existing: { name: string; email: string }[] =
      teamData?.roles?.map((r: any) => ({
        name: r.user?.name ?? "",
        email: r.user?.email?.toLowerCase(),
      })) ?? [];

    if (extractedUsers.length && existing.length) {
      setExtractedUsers((prev) =>
        prev.filter(
          (u) => !existing.some((e) => e.email === u.email?.toLowerCase()),
        ),
      );
    }
  }, [teamData, extractedUsers.length]);

  const { data: groupEmailList = [] } = useQuery({
    queryKey: ["groupEmailList"],
    queryFn: fetchGroupEmailList,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: fetchUserEmails,
    isPending: isGroupEmailLoading,
    isSuccess: isGroupEmailLoaded,
    reset: resetUserEmails,
  } = useMutation({
    mutationFn: (payload: { group?: string; group_id?: string }) =>
      fetchUserGroupEmailList(payload),
    onSuccess: (data: any[]) => {
      const filteredGroupUsers = data.filter(
        (u) => !existingTeamEmails.includes(u.email?.toLowerCase()),
      );

      const enriched: INewRole[] = filteredGroupUsers.map((u) => ({
        ...u,
        right_id: viewerRole.value,
        team_id: team,
      }));

      setExtractedUsers(enriched);
    },
  });

  useEffect(() => {
    if (!teamData?.roles?.length || !extractedUsers.length) return;

    const existingEmails =
      teamData.roles.map((r: any) => r.user.email?.toLowerCase()) ?? [];

    setExtractedUsers((prev) =>
      prev.filter((u) => !existingEmails.includes(u.email?.toLowerCase())),
    );
  }, [teamData, extractedUsers.length]);

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  useEffect(() => {
    if (!isTeamSelected) {
      setSelectedRole(viewerRole);
      setExtractedUsers((prev) =>
        prev.map((u) => ({
          ...u,
          right_id: viewerRole.value,
          team_id: "",
        })),
      );
      return;
    }

    setExtractedUsers((prev) =>
      prev.map((u) => ({
        ...u,
        team_id: team,
      })),
    );
  }, [isTeamSelected, team, viewerRole]);

  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const matchedUserOption = useMemo(
    () =>
      userList.find(
        (u) => String(u.value).toLowerCase() === String(email).toLowerCase(),
      ) ?? null,
    [userList, email],
  );

  const singleUserAlreadyInSelectedTeam =
    !!team &&
    !!email &&
    existingTeamEmails.includes(email.toLowerCase());

  const buildSingleUserPayload = (): INewRole | null => {
    if (!validateEmail(email)) return null;
    if (singleUserAlreadyInSelectedTeam) return null;

    return {
      name: matchedUserOption?.label ?? null,
      email,
      right_id: isTeamSelected ? selectedRole.value : viewerRole.value,
      team_id: team,
    };
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();

    if (isSingleUserMode) {
      const userPayload = buildSingleUserPayload();
      if (!userPayload) return;
      handleAddUser([userPayload], false);
    } else {
      if (!distributionEmail || !extractedUsers.length) return;
      handleAddUser(extractedUsers, true);
    }

    setShowAddUserModal(false);
  };

  const handleModeToggle = () => {
    setIsSingleUserMode((prev) => !prev);
    setEmail("");
    setDistributionEmail(null);
    setExtractedUsers([]);
    resetUserEmails();
    setSelectedRole(viewerRole);
    setTeam(selectedTeam ? String(selectedTeam.id) : "");
  };

  const handleRemoveMember = (member: INewRole) => {
    setExtractedUsers((prev) => prev.filter((m) => m.email !== member.email));
  };

  const canSubmitSingle = isSingleUserMode
    ? validateEmail(email) && !singleUserAlreadyInSelectedTeam
    : false;

  const canSubmitGroup = !isSingleUserMode
    ? !!distributionEmail && extractedUsers.length > 0
    : false;

  const canSubmit = canSubmitSingle || canSubmitGroup;

  return (
    <div className="modal-overlay">
      <div className="modal-panel p-4 w-[550px] relative">
        <h2 className="text-xl title-base-color text-center mb-1">
          {t("adminPanel.addUser")}
        </h2>

        <button
          aria-label="Close"
          className="absolute right-2 top-3 z-10 modal-close-btn"
          onClick={() => setShowAddUserModal(false)}
          type="button"
        >
          <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
        </button>

        <div className="flex items-center space-x-2 mb-2">
          <span className="text-sm text-base-color">
            {t("adminPanel.singleUserMode")}
          </span>
          <button
            type="button"
            onClick={handleModeToggle}
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
              isSingleUserMode ? "bg-del-green" : "bg-gray-400 dark:bg-gray-600"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                isSingleUserMode ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        <form onSubmit={handleAdd}>
          {isSingleUserMode && (
            <>
              <div className="mb-4 text-sm">
                <label className="block title-base-color mb-1 text-base">
                  {t("adminPanel.columns.email")}
                  <span className="text-del-red dark:text-red-400">*</span>
                </label>

                <CreatableSelect<{ value: string; label: string }>
                  value={email ? { value: email, label: email } : null}
                  onChange={(opt) => {
                    if (!opt) {
                      setEmail("");
                      return;
                    }

                    setEmail(opt.value);
                  }}
                  options={userList.filter((u) => {
                    return !existingTeamEmails.includes(u.value.toLowerCase());
                  })}
                  isClearable
                  placeholder={t("adminPanel.emailPlaceholder")}
                  styles={{
                    ...commonStyles,
                    input: (base) => ({
                      ...base,
                      color: darkMode ? "#FFFFFF" : "#000000",
                      fontSize: "14px",
                    }),
                  }}
                  formatCreateLabel={(v) => `Create "${v}"`}
                />
              </div>

              <div className="mb-4 text-sm">
                <label className="block title-base-color mb-1 text-base">
                  {t("adminPanel.columns.team")}
                </label>

                <Select
                  value={selectedTeamOption}
                  options={teamOptions}
                  onChange={(opt) => {
                    const newTeam = opt?.value ?? "";
                    setTeam(newTeam);

                    if (!newTeam) {
                      setSelectedRole(viewerRole);
                    }
                  }}
                  classNamePrefix="react-select"
                  styles={commonStyles}
                />
              </div>

              <div className="mb-4 text-sm">
                <label className="block title-base-color mb-1 text-base">
                  {t("adminPanel.columns.role")}
                  <span className="text-del-red dark:text-red-400">*</span>
                </label>

                {!isTeamSelected ? (
                  <div
                    className="min-h-[38px] rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-3 flex items-center text-sm text-gray-700 dark:text-gray-200"
                  >
                    {viewerRole.label}
                  </div>
                ) : (
                  <Select
                    value={selectedRole}
                    options={roleOptions}
                    onChange={(opt) => {
                      const nextRole = opt ?? viewerRole;
                      setSelectedRole(nextRole);
                    }}
                    required
                    classNamePrefix="react-select"
                    styles={{
                      ...commonStyles,
                      container: (base) => ({
                        ...base,
                        fontSize: "14px",
                      }),
                    }}
                  />
                )}
              </div>

              {singleUserAlreadyInSelectedTeam && (
                <p className="text-sm text-del-red mb-4">
                  {t("adminPanel.userAlreadyInTeam", {
                    defaultValue: "This user is already in the selected team.",
                  })}
                </p>
              )}
            </>
          )}

          {!isSingleUserMode && (
            <>
              <div className="mb-4">
                <label className="block title-base-color mb-1">
                  {t("adminPanel.columns.team")}
                </label>

                <Select
                  value={selectedTeamOption}
                  options={teamOptions}
                  onChange={(opt) => {
                    const newTeam = opt?.value ?? "";
                    setTeam(newTeam);
                    if (!newTeam) {
                      setSelectedRole(viewerRole);
                    }

                    setExtractedUsers((prev) =>
                      prev.map((u) => ({
                        ...u,
                        team_id: newTeam,
                        right_id: newTeam ? u.right_id : viewerRole.value,
                      })),
                    );
                  }}
                  classNamePrefix="react-select"
                  styles={commonStyles}
                />
              </div>

              <label className="block title-base-color mb-1">
                {t("adminPanel.emailGroup")}
              </label>

              <CreatableSelect
                value={distributionEmail || null}
                onChange={(opt) => {
                  setDistributionEmail(opt);

                  if (!opt || !("value" in opt)) {
                    setExtractedUsers([]);
                    resetUserEmails();
                    return;
                  }

                  const isNew = (opt as any).__isNew__ === true;

                  fetchUserEmails(
                    isNew ? { group: opt.value } : { group_id: opt.value },
                  );
                }}
                options={groupEmailList.map((group: IUserData) => ({
                  label: group.name,
                  value: group.id,
                }))}
                isClearable
                placeholder={t("adminPanel.emailGroupPlaceholder")}
                styles={{
                  ...commonStyles,
                  input: (base) => ({
                    ...base,
                    color: darkMode ? "#FFFFFF" : "#000000",
                  }),
                }}
                formatCreateLabel={(v) => `${t("selectPlaceholder")} "${v}"`}
              />

              {isGroupEmailLoading && (
                <p className="text-sm text-gray-500 mt-2">
                  {t("adminPanel.loading")}
                </p>
              )}

              {isGroupEmailLoaded &&
                !isGroupEmailLoading &&
                !extractedUsers.length && (
                  <p className="text-sm text-gray-500">{t("noData")}</p>
                )}

              {extractedUsers.length > 0 && (
                <div className="mt-2 px-1.5 pt-1 max-h-64 overflow-y-auto">
                  <ul className="items-center justify-between mb-2">
                    {extractedUsers.map((u, idx) => (
                      <li
                        key={u.email ?? idx}
                        className="flex items-center justify-between mb-2 bg-gray-100 dark:bg-gray-700 px-2 rounded"
                      >
                        <span className="text-sm text-base-color">
                          {u.name + " <" + u.email + ">"}
                        </span>

                        <div className="flex items-center space-x-2">
                          {!isTeamSelected ? (
                            <div className="min-w-[120px] rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                              {viewerRole.label}
                            </div>
                          ) : (
                            <Select
                              value={
                                roleOptions.find(
                                  (opt) =>
                                    String(opt.value) === String(u.right_id),
                                ) ?? viewerRole
                              }
                              options={roleOptions}
                              onChange={(opt) =>
                                setExtractedUsers((prev) =>
                                  prev.map((item, i) =>
                                    i === idx
                                      ? {
                                          ...item,
                                          right_id: opt?.value ?? viewerRole.value,
                                        }
                                      : item,
                                  ),
                                )
                              }
                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                ...commonStyles,
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 10000,
                                }),
                              }}
                            />
                          )}

                          <hr className="h-8 border-l border-gray-300 dark:border-gray-600 mx-2" />

                          <DeleteButton
                            onClick={() => handleRemoveMember(u)}
                            className="icon-gray focus:outline-none text-del-red hover:text-red-700"
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end space-x-4 mt-2 text-sm">
            <button
              type="button"
              onClick={() => setShowAddUserModal(false)}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              {t("adminPanel.cancel")}
            </button>

            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-4 py-2 ${canSubmit ? "btn-green" : "btn-disabled"}`}
            >
              {t("adminPanel.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { AddUserModal };